import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import useStore from "../store.js";
import button from "../img/ask_button.png";
import askSendingButton from "../img/ask_sending_button.png";
import chatContinueBubble from "../img/chat_continue_bubble.png";
import chatContinueBubbleMobile from "../img/chat_continue_bubble_mobile.png";
import ChatInputMark from "../img/chat_input_mark.svg";
import { useMediaQuery } from "react-responsive";
import ModalPayCheck from "./Modal/ModalPayCheck";

import { gradientBorder } from "../styles/mixin.js";
import { shouldSubmitChatOnKeyDown } from "../utils/chatInput.js";
import { trackNaverSearch } from "../utils/naverConversion.js";

/* 중복되는 css 코드 */
const textStyle = css`
  font-family: Pretendard;
  font-size: 8pt;
  font-style: normal;
  line-height: 26px;
`;

const AskForm = ({
  initialTextValue,
  onMobileInputFocusChange,
  mobileInputBlurSignal = 0,
  mobileInputFocusSignal = 0,
  guestMode = false,
  focusRef,
  directInputRequested = false,
  onDraftChange,
  draftRef,
}) => {
  const {
    setHasAttemptedQuestion,
    setTextValue: setStoreTextValue,
    startSSE,
    startChatSSE,
    isChatMode,
    setIsChatMode,
    addChatMessage,
    chatHistory,
    isAIResponding,
    setIsAIResponding,
    setChatErrorMessage,
    setIsNewChatActive,
    guestSessionComplete,
    guestAvailabilityStatus,
    requestGuestLogin,
  } = useStore();
  const maxLength = guestMode ? 500 : 1000;
  const guestAccessLocked = guestMode && guestSessionComplete;
  const isGuestAvailabilityChecking =
    guestMode && guestAvailabilityStatus === "checking";

  let [inputCount, setInputCount] = useState(0);
  let [textValue, setTextValue] = useState(() => isChatMode ? (draftRef?.current ?? "") : (initialTextValue || "")); // 초기 텍스트 값 설정
  const [showModal, setShowModal] = useState(false); // 질문 검색 로우 차감 확인용 Modal창
  const [showBubble, setShowBubble] = useState(false); // 말풍선 표시 여부

  let [isButtonDisabled, setIsButtonDisabled] = useState(true); // 입력 필드 글자 입력에 따른 버튼의 활성화 여부를 관리
  const internalInputRef = useRef(null);
  const inputRef = focusRef || internalInputRef;
  const isComposingRef = useRef(false);
  const isMobile = useMediaQuery({ maxWidth: 1439 });
  const isWideInput = useMediaQuery({ minWidth: 600 });
  const showDirectInputHint =
    directInputRequested && isChatMode && isWideInput && !isAIResponding && !guestAccessLocked;

  const navigate = useNavigate();

  useEffect(() => {
    onDraftChange?.(textValue);
  }, [onDraftChange, textValue]);

  const focusInputWithoutScroll = useCallback(() => {
    if (!inputRef.current) return;

    try {
      inputRef.current.focus({ preventScroll: true });
    } catch (_) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (isChatMode) {
      const draft = draftRef?.current ?? "";
      setTextValue(draft);
      setInputCount(draft.length);
      setIsButtonDisabled(draft.length === 0);
      return;
    }

    setTextValue(initialTextValue || "");
    setInputCount(initialTextValue ? initialTextValue.length : 0);
    setIsButtonDisabled(!initialTextValue || initialTextValue.length === 0);
  }, [draftRef, initialTextValue, isChatMode]); // 오류시 사용자가 입력한 상황 저장

  useEffect(() => {
    if (!mobileInputBlurSignal || !inputRef.current) return;
    inputRef.current.blur();
  }, [mobileInputBlurSignal]);

  useEffect(() => {
    if (!mobileInputFocusSignal || !inputRef.current) return;
    focusInputWithoutScroll();
  }, [mobileInputFocusSignal, focusInputWithoutScroll]);

  const onInputHandler = (e) => {
    const text = e.target.value;
    setTextValue(text); // 입력된 텍스트 업데이트
    setInputCount(text.length);
    setIsButtonDisabled(text.length === 0); // 입력된 텍스트가 없으면 버튼 비활성화
    setShowBubble(text.length >= 5); // 5글자 이상일 때 말풍선 표시
  };

  const handleChatKeyDown = (e) => {
    if (!shouldSubmitChatOnKeyDown(e, {
      isComposing: isComposingRef.current,
      isButtonDisabled,
      isAIResponding,
    })) {
      return;
    }

    e.preventDefault();
    handleQueryAction();
  };

  // 질문 버튼 눌렀을 때 실행되는 함수
  const handleQueryAction = () => {
    // 답변 생성 중이면 버튼 상태만 화살표로 변경
    if (isAIResponding) {
      setIsAIResponding(false);
      return;
    }

    if (guestAccessLocked) {
      requestGuestLogin("continue");
      return;
    }

    // textValue가 비어있으면 실행하지 않음
    if (!textValue.trim() || isGuestAvailabilityChecking) {
      return;
    }

    onDraftChange?.("");

    // 첫 번째 클릭인 경우 (isChatMode가 false)
    if (!isChatMode) {
      setIsChatMode(true);
      setIsNewChatActive(true); // "새 대화 중" 활성화
      window.history.pushState({ chat: true }, "", window.location.pathname);
    }

    // 대화 기록에 메시지 추가
    addChatMessage(textValue);
    
    // 상태 초기화
    setIsAIResponding(true);
    setChatErrorMessage(''); // 에러 메시지 초기화
    
    // 새로운 채팅 SSE 시작
    startChatSSE(textValue, handleChatComplete, handleChatStart, {
      isGuest: guestMode,
    });
    
    // 입력 필드 초기화
    setTextValue("");
    setInputCount(0);
    setIsButtonDisabled(true);
  };

  const handleChatComplete = (qId) => {
    // 필요시 추가 처리
  };

  const handleChatStart = () => {
    // 필요시 추가 처리
  };

  const handleComplete = (newLogId) => {
      navigate(`/main/q/${newLogId}`, { replace: true });
  };

  const handleSSEStart = () => {
    // SSE 요청이 시작되면 페이지 이동
    navigate(`/main/q/waiting`);
  };

  const handleConfirmModal = () => {
    // 사용자가 충분한 로우 개수를 가진 상태에서 결제 동의를 했을 경우 실행
    setShowModal(false); // 모달 닫기
    setStoreTextValue(textValue);
    setHasAttemptedQuestion();
    trackNaverSearch();
    startSSE(textValue, handleComplete, handleSSEStart);
  };





  // 메시지 전송 후 자동 포커스
  useEffect(() => {
    if (window.innerWidth <= 1140) return;
    focusInputWithoutScroll();
  }, [chatHistory, focusInputWithoutScroll]);

  return (
    <>
      {showDirectInputHint && (
        <DirectInputHint id="chat-direct-input-hint" role="status">
          추가할 내용을 아래 입력창에 적어주세요.
        </DirectInputHint>
      )}
      {!isMobile && (
        guestAccessLocked ? (
          <GuestLockedComposer
            type="button"
            onClick={() => requestGuestLogin("continue")}
            $isInitial={!isChatMode}
            aria-label="로그인하고 대화 계속하기"
          >
            <GuestLockedCopy>
              <GuestLockedTitle>로그인하고 이어가세요</GuestLockedTitle>
            </GuestLockedCopy>
            <GuestLockedAction>로그인하고 계속하기</GuestLockedAction>
          </GuestLockedComposer>
        ) : isChatMode ? (
          /* 채팅 모드: 단순한 단일 인풋 + 외부 전송 버튼 */
          <ChatInputRow>
            <ChatInputWrapper $isAIResponding={isAIResponding}>
              <ChatLogoIcon src={ChatInputMark} alt="" />
              <ChatInputField data-behavior="question-input"
                ref={inputRef}
                type="text"
                placeholder="내용을 입력해주세요"
                aria-describedby={showDirectInputHint ? "chat-direct-input-hint" : undefined}
                onChange={onInputHandler}
                maxLength={maxLength}
                value={textValue}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                }}
                onCompositionEnd={() => {
                  isComposingRef.current = false;
                }}
                onKeyDown={handleChatKeyDown}
                disabled={isGuestAvailabilityChecking}
              />
            </ChatInputWrapper>
            <ChatSendButton data-behavior="question-send"
              type="button"
              onClick={handleQueryAction}
              disabled={
                (isButtonDisabled && !isAIResponding) ||
                isGuestAvailabilityChecking
              }
              $isAIResponding={isAIResponding}
              aria-label={isAIResponding ? "응답 중지" : "채팅 메시지 보내기"}
            >
              {isAIResponding ? (
                <StopSquareIcon />
              ) : (
                <SendArrowIcon disabled={isButtonDisabled} />
              )}
            </ChatSendButton>
          </ChatInputRow>
        ) : (
          /* 일반 모드: 기존 레이아웃 유지 */
          <Wrapper $isChatMode={isChatMode} $isAIResponding={isAIResponding}>
            <ContentBox>
              <InputField data-behavior="question-input"
                ref={inputRef}
                type="text"
                placeholder="내 상황과 원하는 결과를 적어주세요."
                onChange={onInputHandler}
                maxLength={maxLength}
                value={textValue}
                $isChatMode={isChatMode}
                disabled={isGuestAvailabilityChecking}
              />
              <SideField>
                <StyledParagraph
                  $countcolor={inputCount >= 0 ? "#00F370" : "inherit"}>
                  <StyledSpan $countcolor={inputCount >= 0 ? "#00F370" : "inherit"}>
                    {inputCount}
                  </StyledSpan>
                  <SecondSpan> / {maxLength} </SecondSpan>
                </StyledParagraph>
                <BubbleContainer $isChatMode={isChatMode}>
                  <SpeechBubbleImage
                    src={chatContinueBubble}
                    alt="바로 이어서 에이전트와 대화를 통해 상황을 더 자세하게 풀어보세요."
                    $show={showBubble && !isChatMode}
                  />
                  <Button
                    type="button"
                    onClick={handleQueryAction}
                    disabled={
                      (isButtonDisabled && !isAIResponding) ||
                      isGuestAvailabilityChecking
                    }
                    $isChatMode={isChatMode}
                    $isAIResponding={isAIResponding}
                    aria-label={isAIResponding ? "응답 중지" : "비로그인 채팅 시작"}
                    alt="질문하기/검색하기"
                  />
                </BubbleContainer>
              </SideField>
            </ContentBox>
          </Wrapper>
        )
      )}

      {isMobile && (
        guestAccessLocked ? (
          <GuestLockedComposer
            type="button"
            onClick={() => requestGuestLogin("continue")}
            $isInitial={!isChatMode}
            $mobile
            aria-label="로그인하고 대화 계속하기"
          >
            <GuestLockedCopy>
              <GuestLockedTitle>로그인하고 이어가세요</GuestLockedTitle>
            </GuestLockedCopy>
            <GuestLockedAction $mobile>로그인</GuestLockedAction>
          </GuestLockedComposer>
        ) : isChatMode ? (
          /* 채팅 모드: 모바일 */
          <ChatInputRowMobile>
            <ChatInputWrapperMobile
              $isAIResponding={isAIResponding}
              onClick={focusInputWithoutScroll}
            >
              <ChatLogoIcon src={ChatInputMark} alt="" />
              <ChatInputField data-behavior="question-input"
                ref={inputRef}
                type="text"
                placeholder="내용을 입력해주세요"
                aria-describedby={showDirectInputHint ? "chat-direct-input-hint" : undefined}
                onChange={onInputHandler}
                maxLength={maxLength}
                value={textValue}
                onFocus={() => onMobileInputFocusChange?.(true)}
                onBlur={() => onMobileInputFocusChange?.(false)}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                }}
                onCompositionEnd={() => {
                  isComposingRef.current = false;
                }}
                onKeyDown={handleChatKeyDown}
                disabled={isGuestAvailabilityChecking}
              />
            </ChatInputWrapperMobile>
            <ChatSendButton data-behavior="question-send"
              type="button"
              onClick={handleQueryAction}
              disabled={
                (isButtonDisabled && !isAIResponding) ||
                isGuestAvailabilityChecking
              }
              $isAIResponding={isAIResponding}
              aria-label={isAIResponding ? "응답 중지" : "채팅 메시지 보내기"}
            >
              {isAIResponding ? (
                <StopSquareIcon />
              ) : (
                <SendArrowIcon disabled={isButtonDisabled} />
              )}
            </ChatSendButton>
          </ChatInputRowMobile>
        ) : (
          /* 일반 모드: 기존 레이아웃 유지 */
          <WrapperMobile $guestMode={guestMode} $isChatMode={isChatMode} $isAIResponding={isAIResponding}>
            <InputFieldMobile data-behavior="question-input"
              ref={inputRef}
              type="text"
              placeholder="내 상황과 원하는 결과를 적어주세요."
              onChange={onInputHandler}
              maxLength={maxLength}
              value={textValue}
              $isChatMode={isChatMode}
              onFocus={() => onMobileInputFocusChange?.(true)}
              onBlur={() => onMobileInputFocusChange?.(false)}
              disabled={isGuestAvailabilityChecking}
            />
            <MobileInputCount aria-label={`${inputCount} / ${maxLength}`}>
              <StyledSpan $countcolor="#00F370">{inputCount}</StyledSpan>
              <SecondSpan> / {maxLength}</SecondSpan>
            </MobileInputCount>
            <SideFieldMobile $isChatMode={isChatMode}>
              <BubbleContainerMobile $isChatMode={isChatMode}>
                <SpeechBubbleImageMobile
                  src={chatContinueBubbleMobile}
                  alt="이어서 대화를 통해 상황을 더 자세히 풀어보세요."
                  $show={showBubble && !isChatMode}
                />
                <Button
                  type="button"
                  onClick={handleQueryAction}
                  disabled={
                    (isButtonDisabled && !isAIResponding) ||
                    isGuestAvailabilityChecking
                  }
                  $isChatMode={isChatMode}
                  $isAIResponding={isAIResponding}
                  aria-label={isAIResponding ? "응답 중지" : "비로그인 채팅 시작"}
                  alt="질문하기/검색하기"
                />
              </BubbleContainerMobile>
            </SideFieldMobile>
          </WrapperMobile>
        )
      )}

      <ModalPayCheck
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmModal}
        law={10}
        buttonType="queryButton"
        headerContent="검색하기"
        additionalContent={
          <span
            dangerouslySetInnerHTML={{
              __html:
                "사용자의 상황과 유사한 최대 5개의 판례를 검색 결과로<br />제공하며, 검색 시마다 다른 결과를 얻을 수 있습니다.",
            }}
          />
        }
      />

    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  ${({ $isAIResponding }) => !$isAIResponding && gradientBorder}

  width: 680px;
  height: ${({ $isChatMode }) => ($isChatMode ? '114px' : '200px')};
  border-radius: 24px;
  background: #fff;
  box-shadow: 0px 4px 20px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: row;
  // transition: height 0.4s cubic-bezier(0.4, 0, 0.2, 1); // 윗부분의 선이 스르륵 작아지는 효과 넣고싶은데 조금 어려움
  overflow: hidden; /* 부드러운 효과를 위해 필요 */
`;

const ContentBox = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: inherit;
  // height: ${({ isChatMode }) => (isChatMode ? 'inherit' : '100%')};
  // height: ${({ isChatMode }) => (isChatMode ? '10%' : '100%')};
  display: flex;
`;

const WrapperMobile = styled.div`
  ${({ $isAIResponding }) => !$isAIResponding && gradientBorder}

  position: relative;
  width: 624px;
  max-width: calc(100vw - 48px);
  height: ${({ $isChatMode }) => ($isChatMode ? '114px' : '255px')};
  // height: 255px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0px 4px 20px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;

  @media (max-width: 10000px) and (min-width: 1141px) {
    width: 680px;
    height: ${({ $isChatMode }) => ($isChatMode ? '114px' : '200px')};
    flex-direction: row;
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      width: 531px;
      height: ${({ $isChatMode, $guestMode }) => ($isChatMode ? '128px' : ($guestMode ? '220px' : '396px'))};
      flex-direction: row;
    }
  }

  @media not all and (min-width: 600px) {
    width: auto;
    align-self: stretch;
    margin-right: 24px;
    margin-left: 24px;
    // height: ${({ isChatMode }) => (isChatMode ? '128px' : '325px')};
    height: ${({ $isChatMode }) => ($isChatMode ? '128px' : '160px')};
  }
`;

const MobileInputCount = styled.p`
  display: none;

  @media not all and (min-width: 600px) {
    position: absolute;
    bottom: 23px;
    left: 16px;
    display: flex;
    align-items: center;
    height: 17px;
    margin: 0;
  }
`;

const InputField = styled.textarea`
  flex-shrink: 0;
  width: 522px;
  border: none;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  color: #000; /* 기본 텍스트 색상을 검은색으로 설정 */
  /* 텍스트를 맨 위로 배치하는 스타일 */
  vertical-align: top; /* 텍스트를 요소의 맨 위로 배치 */
  margin: 24px 18px 24px 32px;
  padding: 0; /* textarea에 기본적으로 들어가있는 설정 제거 */
  padding-right: 20px;
  resize: none;
  overflow-y: auto;
  margin-bottom: 24px;

  /* 선택될 때 나타나는 테두리 제거 */
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: rgba(0, 0, 0, 0.3); /* 플레이스홀더 텍스트 색상 설정 */
  }

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
`;

const InputFieldMobile = styled.textarea`
  flex-shrink: 0;
  border: none;
  height: 156px;
  font-family: Pretendard;
  line-height: 23px;
  font-size: 16px;
  padding: 0; /* textarea 기본 설정된 padding 값 제거 */

  @media (max-width: 10000px) and (min-width: 1141px) {
    width: 522px;
    height: auto;
    margin: 24px 18px 24px 32px;
    padding-right: 20px;
    margin-bottom: ${({ $isChatMode }) => ($isChatMode ? '24px' : '90px')}; /* Chat 모드일 때 24px, 일반 모드일 때 90px */
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      width: 373px;
      height: ${({ $isChatMode }) => ($isChatMode ? '64px' : 'auto')};
      margin: 24px 8px 24px 32px;
      padding-right: 30px;
      margin-bottom: 80px; // (25.08.07) 임시 - 작은 말풍선 가림 방지
    }
  }

  @media not all and (min-width: 600px) {
    //height: ${({ isChatMode }) => (isChatMode ? '64px' : '237px')};
    height: ${({ $isChatMode }) => ($isChatMode ? '64px' : '80px')};
    margin: 16px 16px 0px 16px;
  }

  /* 텍스트를 맨 위로 배치하는 스타일 */
  vertical-align: top; /* 텍스트를 요소의 맨 위로 배치 */
  margin: 16px 10px 0px 16px; /* 스크롤 오른쪽 10px 떨어지기 */
  resize: none; /* 크기 조절 비활성화 */
  overflow-y: auto;

  /* 선택될 때 나타나는 테두리 제거 */
  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.3); /* 플레이스홀더 텍스트 색상 설정 */
  }

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 10px; /* 스크롤바의 전체 너비를 설정 */
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.05); /* 스크롤바 막대 색상 */
    border-radius: 4px;
    border: 2px solid rgba(255, 255, 255, 1); /* 스크롤바와 내용 사이에 여백을 주기 위한 테두리 */
  }
  &::-webkit-scrollbar-track {
    background-color: rgba(255, 255, 255, 1); /* 스크롤바 트랙 색상 */
    margin: 8px 0; /* 스크롤 위로부터 24px 맞추기 위해 margin-top 16px과 합작 */
  }
`;

const SideField = styled.div`
  width: auto;
  display: flex;
  flex-direction: column;
  align-items: center; /* 자식 요소 수직 가운데 정렬 */
  margin-right: 24px;
`;

const SideFieldMobile = styled.div`
  width: auto;
  display: flex;
  flex-direction: row;
  margin-top: auto;
  margin-left: 8%;

  Button {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    margin-left: 50%;

    @media (max-width: 10000px) and (min-width: 1141px) {
      width: 64px;
      height: 64px;
      margin-left: 0;
    }

    @media not all and (min-width: 1141px) {
      @media (min-width: 600px) {
        margin-left: 0;
        margin-top: 47px;
      }
    }

    @media not all and (min-width: 600px) {
      margin-right: 16px;
      margin-left: auto;
      margin-top: 0px;
      width: 40px;
      height: 40px;
      margin-bottom: 20px;
    }
  }
  StyledParagraph {
    align-items: flex-end;
  }

  @media (max-width: 10000px) and (min-width: 1141px) {
    flex-direction: column;
    margin-top: ${({ $isChatMode }) => ($isChatMode ? 'auto' : '0px')};
    margin-left: 0;
    margin-bottom: ${({ $isChatMode }) => ($isChatMode ? '15px;' : '0px')};
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      flex-direction: column;
      align-items: center;
      width: 100%;
      margin-top: 15px;
      margin-left: 0;
    }
  }

`;

const StyledParagraph = styled.p`
  color: ${(props) => props.$countcolor};
  text-align: center;
  width: 64px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 24px;
  border: 0.5px solid rgba(0, 0, 0, 0.3);
  margin-top: 18px;
`;

const StyledSpan = styled.span`
  ${textStyle}
  color: ${(props) => props.$countcolor};
  font-weight: 700;
`;

const SecondSpan = styled.span`
  ${textStyle}
  color: rgba(0, 0, 0, 0.3);
  font-weight: 400;
  margin-left: 2px;
`;

const Button = styled.button`
  // margin-top: auto; /* 상단 여백 추가 */
  margin-bottom: ${props => props.$isChatMode ? '0px' : '24px'}; /* (25.06.25) 임시로 */
  margin-left: 10px;
  background: url(${props => props.$isAIResponding ? askSendingButton : button}) no-repeat center center;
  background-size: cover;
  width: ${props => props.$isChatMode ? '40px' : '56px'}; /* 이미지의 너비에 맞춤 */
  height: ${props => props.$isChatMode ? '40px' : '56px'}; /* 이미지의 높이에 맞춤 */
  border: none; /* 테두리 제거 */
  cursor: pointer; /* 마우스 커서를 포인터로 변경 */

  /* 버튼이 비활성화 상태일 때의 스타일 */
  &:disabled {
    pointer-events: none;
    background-blend-mode: normal; /* blend 모드 해제 */
    opacity: 0.5; /* 투명도를 낮춰서 비활성화 상태를 나타냄 */
  }

  /* 활성화와 호버가 둘 다 충족됐을 때 */
  &:hover:enabled {
    background-color: rgba(255, 255, 255, 0.3); /* 마우스 hover시 배경색 추가 */
    background-blend-mode: screen; /* 배경 이미지와 색상 혼합 모드 설정 */
  }

  @media not all and (min-width: 600px) {
    margin-bottom: 16px;
  }
`;

// 말풍선 컨테이너 스타일
const BubbleContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: ${props => props.$isChatMode ? '0px' : 'auto'};
`;

const BubbleContainerMobile = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  
  @media (max-width: 10000px) and (min-width: 1141px) {
    gap: 12px;
    margin-top: ${props => props.$isChatMode ? '0px' : 'auto'};
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      gap: 12px;
      margin-top: ${props => props.$isChatMode ? '0px' : 'auto'};
    }
  }

  @media not all and (min-width: 600px) {
    margin-right: 0px;
    margin-left: auto;
    margin-top: ${props => props.$isChatMode ? '0px' : 'auto'};
  }
`;

// PC/Tablet 말풍선 이미지 스타일
const SpeechBubbleImage = styled.img`
  width: 418px;
  height: 43px;
  position: absolute;
  right: 76px;
  transform: translateY(-25%);
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 100; /* 앱 스토어 QR 팝업이 z-index 150이라 말풍선을 100으로 다운*/
`;

// Mobile 말풍선 이미지 스타일
const SpeechBubbleImageMobile = styled.img`
  width: 228px;
  height: 36px;
  position: absolute;
  top: 50%;
  right: 68px;
  transform: translateY(-75%);
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 1000;

  @media (max-width: 10000px) and (min-width: 1141px) {
    width: 310px; /* 내가 임의로 w,h 값 정해서 적용 */
    height: 42px;  
    right: 72px;
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      width: 280px; /* 내가 임의로 w,h 값 정해서 적용 */
      height: 38px;
      top: 67%;
      right: 54px;
    }
  }
  
  @media (max-width: 375px) {
    width: 180px; /* 내가 임의로 w,h 값 정해서 적용 */
    height: 30px; 
    top: 50%;
    right: 66px;
  }
`;

/* ─── 채팅 모드 인풋 (버튼 외부 분리) ─── */

const DirectInputHint = styled.p`
  width: 680px;
  box-sizing: border-box;
  margin: 0 0 8px;
  color: rgba(0, 0, 0, 0.68);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  word-break: keep-all;
  overflow-wrap: anywhere;

  @media (max-width: 1439px) {
    width: 100%;
    max-width: 536px;
    padding: 0 24px;
  }
`;

const ChatInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 680px;
`;

const ChatInputRowMobile = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 536px;
  padding: 0 24px;
  box-sizing: border-box;

  @media (max-width: 1140px) and (min-width: 600px) {
    width: 100%;
  }
`;

const ChatInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  box-sizing: border-box;
  padding: 0 20px;
  border-radius: 32px;
  border: 1px solid #00f370;
  background: #fff;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.05);
  opacity: ${({ $isAIResponding }) => $isAIResponding ? 0.6 : 1};
  transition: opacity 0.2s ease;
`;

const ChatInputWrapperMobile = styled(ChatInputWrapper)`
  height: 48px;
  padding: 0 16px;
`;

const ChatLogoIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 1;
`;

const ChatInputField = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  color: rgba(0, 0, 0, 0.8);
  letter-spacing: -0.32px;

  &::placeholder {
    color: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 599px) {
    font-size: 16px;
  }
`;

const ChatSendButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  /* 활성: PNG 화살표는 오른쪽이라, 입력창과 동일 톤의 그라데이션 + 위쪽 SVG 화살표만 사용 */
  background: ${({ disabled, $isAIResponding }) =>
    $isAIResponding
      ? 'rgba(0, 0, 0, 0.15)'
      : disabled
        ? '#F5F5F5'
        : 'linear-gradient(135deg, #00f370 0%, #0057ff 100%)'};
  background-size: auto;
  cursor: pointer;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:disabled {
    cursor: default;
    opacity: 1;
  }

  &:hover:enabled {
    opacity: 0.9;
  }
`;

const SendArrowIcon = ({ disabled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 18 18"
    fill="none"
    style={{ transform: "rotate(-90deg)", flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M3.75 9H14.25M14.25 9L9.75 4.5M14.25 9L9.75 13.5"
      stroke={disabled ? "rgba(0, 0, 0, 0.22)" : "white"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StopSquareIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" rx="2" fill="white" />
  </svg>
);

const GuestLockedComposer = styled.button`
  width: 680px;
  min-height: ${({ $isInitial }) => ($isInitial ? "80px" : "64px")};
  box-sizing: border-box;
  padding: 12px 16px 12px 24px;
  border: 1px solid #00f370;
  border-radius: ${({ $isInitial }) => ($isInitial ? "24px" : "32px")};
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #fcfffd;
  }

  @media (max-width: 1140px) {
    width: calc(100% - 48px);
    max-width: 536px;
    min-height: ${({ $isInitial }) => ($isInitial ? "80px" : "72px")};
    margin: ${({ $isInitial }) => ($isInitial ? "0 24px" : "0")};
    padding: 12px 14px 12px 18px;
    border-radius: 24px;
  }
`;

const GuestLockedCopy = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
`;

const GuestLockedTitle = styled.span`
  color: rgba(0, 0, 0, 0.8);
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.3px;

  @media (max-width: 599px) {
    font-size: 13px;
    line-height: 20px;
  }
`;

const GuestLockedAction = styled.span`
  height: 40px;
  padding: 0 18px;
  border-radius: 24px;
  background: linear-gradient(104deg, #6ef4ac 22%, #78a5fe 87%);
  color: rgba(0, 0, 0, 0.8);
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;
  line-height: 40px;
  white-space: nowrap;

  @media (max-width: 599px) {
    height: 36px;
    padding: 0 14px;
    font-size: 12px;
    line-height: 36px;
  }
`;

export default AskForm;
